import { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { DataGrid } from 'react-data-grid';
import 'react-data-grid/lib/styles.css';

type XLSXViewerProps = {
  fileUrl?: string;
};

type SheetData = {
  name: string;
  rows: Record<string, any>[];
  columns: { key: string; name: string; resizable: boolean }[];
  titleRows: string[][]; // Rows above the table header
};

export function XLSXViewer({ fileUrl }: XLSXViewerProps) {
  const [sheets, setSheets] = useState<SheetData[]>([]);
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);
  const [localFileUrl, setLocalFileUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeFileUrl = fileUrl ?? localFileUrl;

  useEffect(() => {
    if (!activeFileUrl) return;

    const fetchXLSX = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('XLSXViewer - Fetching XLSX from:', activeFileUrl);

        const response = await fetch(activeFileUrl);
        console.log('XLSXViewer - Response status:', response.status);

        if (!response.ok) {
          throw new Error(`Failed to fetch XLSX: ${response.status}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        console.log('XLSXViewer - Sheet names:', workbook.SheetNames);

        const allSheets: SheetData[] = [];

        // Process all sheets
        for (const sheetName of workbook.SheetNames) {
          const worksheet = workbook.Sheets[sheetName];

          // Convert sheet to raw data with header option to get all rows
          const rawData = XLSX.utils.sheet_to_json<any[]>(worksheet, {
            header: 1,
            defval: '',
            blankrows: false
          });

          console.log(`XLSXViewer - Sheet "${sheetName}" raw data:`, rawData);

          // Find the header row (first row with multiple non-empty values)
          let headerRowIndex = -1;
          for (let i = 0; i < rawData.length; i++) {
            const row = rawData[i];
            const nonEmptyCount = row.filter((cell: any) => cell !== '' && cell !== null && cell !== undefined).length;
            if (nonEmptyCount >= 2) {
              headerRowIndex = i;
              break;
            }
          }

          if (headerRowIndex === -1 || headerRowIndex >= rawData.length - 1) {
            console.warn(`XLSXViewer - No valid data found in sheet "${sheetName}"`);
            continue;
          }

          // Get title rows (all rows before header)
          const titleRows = rawData.slice(0, headerRowIndex);
          const headerRow = rawData[headerRowIndex];
          const dataRows = rawData.slice(headerRowIndex + 1);

          console.log(`XLSXViewer - Title rows (${titleRows.length}):`, titleRows);
          console.log(`XLSXViewer - Header row at index ${headerRowIndex}:`, headerRow);
          console.log(`XLSXViewer - Data rows count:`, dataRows.length);

          // Create columns from header
          const columns = headerRow
            .map((header: any, index: number) => {
              const columnKey = header?.toString().trim() || `Column_${index}`;
              return {
                key: columnKey,
                name: columnKey,
                resizable: true,
              };
            })
            .filter((col: any) => col.name !== '');

          // Convert data rows to objects
          const rows = dataRows
            .map((row: any[]) => {
              const rowObj: Record<string, any> = {};
              columns.forEach((col, index) => {
                rowObj[col.key] = row[index] !== undefined ? row[index] : '';
              });
              // Only include rows that have at least one non-empty value
              const hasData = Object.values(rowObj).some(val => val !== '');
              return hasData ? rowObj : null;
            })
            .filter(row => row !== null) as Record<string, any>[];

          console.log(`XLSXViewer - Processed ${rows.length} rows for sheet "${sheetName}"`);

          if (rows.length > 0 && columns.length > 0) {
            allSheets.push({
              name: sheetName,
              rows,
              columns,
              titleRows,
            });
          }
        }

        setSheets(allSheets);
        if (allSheets.length === 0) {
          setError('No valid data found in the Excel file');
        }
      } catch (err) {
        console.error('XLSXViewer - Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load XLSX');
      } finally {
        setLoading(false);
      }
    };

    fetchXLSX();
  }, [activeFileUrl]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const blobUrl = URL.createObjectURL(file);
      setLocalFileUrl(blobUrl);
    }
  };

  if (!activeFileUrl) {
    return (
      <div>
        <input type="file" accept=".xlsx,.xls" onChange={handleFileChange} />
        <p>XLSX</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">Loading Excel file...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-red-500">Error: {error}</p>
      </div>
    );
  }

  if (sheets.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No data to display</p>
      </div>
    );
  }

  const currentSheet = sheets[activeSheetIndex];

  return (
    <div className="h-full flex flex-col">
      {/* Sheet tabs if multiple sheets */}
      {sheets.length > 1 && (
        <div className="flex gap-2 border-b border-gray-200 mb-2 pb-2">
          {sheets.map((sheet, index) => (
            <button
              key={sheet.name}
              onClick={() => setActiveSheetIndex(index)}
              className={`px-3 py-1 text-sm rounded transition-colors ${
                index === activeSheetIndex
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {sheet.name}
            </button>
          ))}
        </div>
      )}

      {/* Data content */}
      <div className="flex-1 overflow-auto p-4 bg-white border border-gray-250v2 rounded-lg">
        {/* Title rows - displayed above the table */}
        {currentSheet.titleRows.length > 0 && (
          <div className="mb-4">
            {currentSheet.titleRows.map((row, rowIndex) => {
              const text = row.filter(cell => cell !== '' && cell !== null && cell !== undefined).join(' ');
              if (!text) return null;
              return (
                <div
                  key={rowIndex}
                  className="text-zinc-900 text-base mb-2"
                >
                  {text}
                </div>
              );
            })}
          </div>
        )}

        {/* Data grid */}
        <DataGrid
          columns={currentSheet.columns}
          rows={currentSheet.rows}
          className="rdg-light min-w-[926px] border-0 no-focus-outline"
          rowHeight={32}
          rowClass={() => 'text-zinc-900'}
          headerRowHeight={38}
          headerRowClass="text-gray-550 bg-gray-50 text-sm font-medium"
        />
      </div>
    </div>
  );
}
